import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  Clock,
  CreditCard,
  Download,
  Loader2,
  Plus,
  Settings as SettingsIcon,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  billingApi,
  type CreditHistoryType,
  type CreditPack,
  type CreditTransaction,
  type CreditWallet,
  type Plan,
  type PlatformUsage,
  type QuotaItem,
  type Subscription,
} from "@/features/billing/api";
import { cn } from "@/lib/utils";

const LIMIT_LABELS: Record<string, string> = {
  max_agents: "AI Agents",
  max_phone_numbers: "Phone Numbers",
  max_team_members: "Team Members",
  max_knowledge_documents: "Knowledge Docs",
  included_minutes: "Call Minutes/mo",
};

const QUOTA_LABELS: Record<string, string> = {
  agents: "AI Agents",
  phone_numbers: "Phone Numbers",
  team_members: "Team Members",
  knowledge_documents: "Knowledge Docs",
  minutes: "Call Minutes",
};

export default function BillingPage() {
  const plansQ = useQuery({
    queryKey: ["billing", "plans"],
    queryFn: billingApi.plans,
    staleTime: 5 * 60_000,
  });
  const invoicesQ = useQuery({
    queryKey: ["billing", "invoices"],
    queryFn: billingApi.invoices,
    staleTime: 60_000,
  });
  const subQ = useQuery({
    queryKey: ["billing", "subscription"],
    queryFn: billingApi.subscription,
    staleTime: 30_000,
  });
  const quotaQ = useQuery({
    queryKey: ["billing", "quota"],
    queryFn: billingApi.quota,
    staleTime: 30_000,
  });
  const platformQ = useQuery({
    queryKey: ["billing", "platform-usage"],
    queryFn: billingApi.platformUsage,
    staleTime: 30_000,
  });
  const creditQ = useQuery({
    queryKey: ["billing", "credit"],
    queryFn: billingApi.credit,
    staleTime: 30_000,
  });
  const creditPacksQ = useQuery({
    queryKey: ["billing", "credit-packs"],
    queryFn: billingApi.creditPacks,
    staleTime: 5 * 60_000,
  });

  const [planDialogOpen, setPlanDialogOpen] = useState(false);
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const location = useLocation();

  // The header credit chip deep-links here with ?buy=credit so a click always
  // opens the top-up dialog — including when the user is already on /billing,
  // where a plain link would look dead.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("buy") === "credit") {
      setCreditDialogOpen(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [location.search]);

  // Handle ?checkout / ?credit return from Stripe
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const c = params.get("checkout");
    if (c === "success") {
      toast.success("Subscription activated successfully!");
      window.history.replaceState({}, "", window.location.pathname);
    } else if (c === "cancelled") {
      toast.error("Checkout cancelled.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    const cd = params.get("card");
    if (cd === "success") {
      toast.success("Card saved.");
      queryClient.invalidateQueries({ queryKey: ["billing", "payment-method"] });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (cd === "cancelled") {
      toast.error("Card setup cancelled.");
      window.history.replaceState({}, "", window.location.pathname);
    }
    const cr = params.get("credit");
    if (cr === "success") {
      toast.success("Credit added! It may take a moment to reflect.");
      queryClient.invalidateQueries({ queryKey: ["billing", "credit"] });
      window.history.replaceState({}, "", window.location.pathname);
    } else if (cr === "cancelled") {
      toast.error("Credit purchase cancelled.");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, [queryClient]);

  const portal = useMutation({
    mutationFn: () => billingApi.portal(),
    onSuccess: (data) => {
      window.location.href = data.portal_url;
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error || "Failed to open billing portal.");
    },
  });

  const loading =
    plansQ.isLoading || invoicesQ.isLoading || subQ.isLoading || quotaQ.isLoading;

  const subscription = subQ.data || null;
  const plans = plansQ.data ?? [];
  const invoices = invoicesQ.data ?? [];
  const quota = quotaQ.data;
  const minutes = quota?.minutes;
  // Show the generic resource grid without minutes — minutes get their own
  // meter with top-up purchase below.
  const otherQuota = quota
    ? Object.fromEntries(
        Object.entries(quota).filter(([k]) => k !== "minutes"),
      )
    : {};

  const hasActive = subscription?.state === "active";

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Billing</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage your subscription and view invoices.
        </p>
      </div>

      <CurrentPlanCard
        subscription={subscription}
        hasActive={hasActive}
        portalPending={portal.isPending}
        onPortal={() => portal.mutate()}
        onUpgrade={() => setPlanDialogOpen(true)}
      />

      {platformQ.data?.enabled && <PlatformUsageCard usage={platformQ.data} />}

      {creditQ.data?.enabled && (
        <PrepaidCreditCard
          wallet={creditQ.data}
          canBuy={(creditPacksQ.data ?? []).length > 0}
          onBuy={() => setCreditDialogOpen(true)}
        />
      )}

      {creditQ.data?.enabled && (
        <PaymentMethodCard packs={creditPacksQ.data ?? []} />
      )}

      {Object.keys(otherQuota).length > 0 && <QuotaCard quota={otherQuota} />}

      <InvoicesCard invoices={invoices} />

      <ChoosePlanDialog
        open={planDialogOpen}
        onOpenChange={setPlanDialogOpen}
        plans={plans}
        subscription={subscription}
      />

      <CreditDialog
        open={creditDialogOpen}
        onOpenChange={setCreditDialogOpen}
        packs={creditPacksQ.data ?? []}
      />
    </div>
  );
}

function money(cents: number) {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Live "invoice building up" under the platform two-rate model:
 *  active customers × per-customer fee + minutes used × per-minute fee. */
function PlatformUsageCard({ usage }: { usage: PlatformUsage }) {
  const rates = usage.rates!;
  const perMinute = `${rates.per_minute_cents.toLocaleString(undefined, { maximumFractionDigits: 3 })}¢`;
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Platform usage this period</CardTitle>
        <p className="text-sm text-muted-foreground">
          {money(rates.per_customer_cents)} per customer / month, billed in
          advance + {perMinute} per minute, paid from your prepaid credit.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" /> Customers
            </div>
            <p className="mt-1 text-xl font-semibold">{usage.customers!.count}</p>
            <p className="text-sm text-muted-foreground">= {money(usage.customers!.fee_cents)}</p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" /> Minutes used
            </div>
            <p className="mt-1 text-xl font-semibold">{usage.minutes!.used}</p>
            <p className="text-sm text-muted-foreground">
              = {money(usage.minutes!.fee_cents)} from credit
            </p>
          </div>
          <div className="rounded-lg border bg-muted/40 p-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CreditCard className="h-4 w-4" /> Total this period
            </div>
            <p className="mt-1 text-xl font-semibold">{money(usage.total_cents!)}</p>
            <p className="text-sm text-muted-foreground">customers + minutes</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Prepaid pay-as-you-go wallet for per-minute call charges (Twilio-style):
 *  buy credit up front, minutes deduct it, calls stop at zero. */
function PrepaidCreditCard({
  wallet,
  canBuy,
  onBuy,
}: {
  wallet: CreditWallet;
  canBuy: boolean;
  onBuy: () => void;
}) {
  const balance = wallet.balance_cents ?? 0;
  const low = wallet.low_threshold_cents ?? 0;
  const empty = balance <= 0;
  const isLow = !empty && low > 0 && balance < low;
  const perMinute = `${(wallet.per_minute_cents ?? 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}¢`;
  const txns = wallet.transactions ?? [];
  const [historyOpen, setHistoryOpen] = useState(false);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Prepaid Call Credit</CardTitle>
        <Button size="sm" onClick={onBuy} disabled={!canBuy}>
          <Plus className="size-4" />
          Buy Credit
        </Button>
      </CardHeader>
      <CardContent>
        <p
          className={cn(
            "text-3xl font-semibold tabular-nums",
            empty
              ? "text-rose-600 dark:text-rose-400"
              : isLow
              ? "text-amber-600 dark:text-amber-400"
              : "",
          )}
        >
          {money(Math.max(balance, 0))}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          Calls cost {perMinute} per minute, paid from this balance.
          {empty &&
            " Balance used up — new calls are blocked until you buy credit."}
          {isLow && " Balance is running low."}
          {!canBuy && " (No credit packs available — contact support.)"}
        </p>

        {txns.length > 0 && (
          <div className="mt-4 border-t pt-3">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Recent activity
              </p>
              <Button
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs"
                onClick={() => setHistoryOpen(true)}
              >
                View full history
              </Button>
            </div>
            <ul className="space-y-1.5 text-sm">
              {txns.slice(0, 5).map((t) => (
                <li key={t.id} className="flex items-center justify-between">
                  <span className="text-muted-foreground">
                    {new Date(t.created_at).toLocaleDateString()} —{" "}
                    {txnLabel(t)}
                  </span>
                  <span
                    className={cn(
                      "tabular-nums",
                      t.amount_cents >= 0
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "",
                    )}
                  >
                    {t.amount_cents >= 0 ? "+" : "−"}
                    {money(Math.abs(t.amount_cents))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
      <WalletHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} />
    </Card>
  );
}

function txnLabel(t: CreditTransaction) {
  return (
    t.description ||
    (t.kind === "topup"
      ? "Credit purchase"
      : t.kind === "adjustment"
      ? "Adjustment"
      : "Call usage")
  );
}

const HISTORY_FILTERS: { value: CreditHistoryType | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "topup", label: "Credit purchases" },
  { value: "fee", label: "Customer fees" },
  { value: "call", label: "Calls" },
  { value: "adjustment", label: "Adjustments" },
];

/** Every wallet movement — purchases, monthly customer fees, call minutes,
 *  adjustments — newest first, with the balance after each one. */
function WalletHistoryDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [filter, setFilter] = useState<CreditHistoryType | "all">("all");
  const [page, setPage] = useState(1);
  const historyQ = useQuery({
    queryKey: ["billing", "credit-history", filter, page],
    queryFn: () =>
      billingApi.creditTransactions({
        page,
        type: filter === "all" ? undefined : filter,
      }),
    enabled: open,
    placeholderData: (prev) => prev,
  });
  const rows = historyQ.data?.results ?? [];
  const meta = historyQ.data?.pagination;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Wallet history</DialogTitle>
          <DialogDescription>
            Every credit purchase, monthly customer fee and call charge.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-1.5">
          {HISTORY_FILTERS.map((f) => (
            <Button
              key={f.value}
              size="sm"
              variant={filter === f.value ? "default" : "outline"}
              onClick={() => {
                setFilter(f.value);
                setPage(1);
              }}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <div className="max-h-[55vh] overflow-auto rounded-md border">
          {historyQ.isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Nothing here yet.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-muted/60 text-left text-xs text-muted-foreground backdrop-blur">
                <tr>
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium">Description</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                  <th className="px-3 py-2 text-right font-medium">Balance</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} className="border-t">
                    <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">
                      {new Date(t.created_at).toLocaleString(undefined, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </td>
                    <td className="px-3 py-2">{txnLabel(t)}</td>
                    <td
                      className={cn(
                        "whitespace-nowrap px-3 py-2 text-right tabular-nums",
                        t.amount_cents >= 0
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "",
                      )}
                    >
                      {t.amount_cents >= 0 ? "+" : "−"}
                      {money(Math.abs(t.amount_cents))}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {money(t.balance_after_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {meta && meta.total_pages > 1 && (
          <DialogFooter className="items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">
              Page {meta.page} of {meta.total_pages} · {meta.total} entries
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={!meta.has_previous || historyQ.isFetching}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!meta.has_next || historyQ.isFetching}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreditDialog({
  open,
  onOpenChange,
  packs,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  packs: CreditPack[];
}) {
  const queryClient = useQueryClient();
  // With a card on file the purchase happens right here; without one we still
  // have to send them to Stripe to enter card details.
  const cardQ = useQuery({
    queryKey: ["billing", "payment-method"],
    queryFn: billingApi.paymentMethod,
    enabled: open,
    staleTime: 30_000,
  });
  const hasCard = !!cardQ.data?.saved;

  // Two shapes: a redirect URL (no card) or an applied charge (card on file).
  const checkout = useMutation<
    { checkout_url: string } | { ok: boolean; balance_cents: number },
    unknown,
    CreditPack
  >({
    mutationFn: (pack: CreditPack) =>
      hasCard
        ? billingApi.creditQuickTopUp({ pack_id: pack.id })
        : billingApi.creditCheckout({ pack_id: pack.id }),
    onSuccess: (data) => {
      if ("checkout_url" in data) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.success("Credit added.");
      queryClient.invalidateQueries({ queryKey: ["billing"] });
      onOpenChange(false);
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error || "Payment failed.");
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Buy Call Credit</DialogTitle>
          <DialogDescription>
            Prepaid credit pays your per-minute call charges and never
            expires. Calls stop when it runs out.
            {hasCard && cardQ.data?.last4
              ? ` Charged to your saved card ending ${cardQ.data.last4}.`
              : ""}
          </DialogDescription>
        </DialogHeader>

        {packs.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No credit packs available. Contact support.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {packs.map((pack) => {
              const pending =
                checkout.isPending && checkout.variables?.id === pack.id;
              return (
                <Card key={pack.id} className="flex flex-col">
                  <CardContent className="flex flex-1 flex-col gap-2 p-4">
                    <p className="text-lg font-semibold">{pack.name}</p>
                    <p className="text-2xl font-bold text-primary">
                      {money(pack.amount_cents)}
                      <span className="ml-1 text-sm font-normal text-muted-foreground">
                        {pack.currency}
                      </span>
                    </p>
                    <Button
                      className="mt-auto"
                      disabled={pending}
                      onClick={() => checkout.mutate(pack)}
                    >
                      {pending
                        ? hasCard
                          ? "Charging…"
                          : "Redirecting…"
                        : hasCard
                        ? "Buy now"
                        : "Buy"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CurrentPlanCard({
  subscription,
  hasActive,
  portalPending,
  onPortal,
  onUpgrade,
}: {
  subscription: Subscription | null;
  hasActive: boolean;
  portalPending: boolean;
  onPortal: () => void;
  onUpgrade: () => void;
}) {
  const state = subscription?.state ?? "none";
  const chipCls =
    state === "trialing"
      ? "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400"
      : state === "active"
      ? "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400"
      : state === "past_due" || state === "cancelled"
      ? "text-rose-600 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-400"
      : "text-muted-foreground bg-muted";

  // Big heading: the real plan name, or a state word — never "Trial" once it's over.
  const heading =
    state === "trialing"
      ? "Free Trial"
      : subscription?.plan_name || "No active plan";

  // Small chip text next to the heading.
  const chipText =
    state === "active"
      ? "active"
      : state === "trialing"
      ? "trial"
      : state === "past_due"
      ? "past due"
      : state === "cancelled"
      ? "cancelled"
      : "no plan";

  function statusLabel() {
    if (state === "trialing") {
      const days = subscription?.trial_days_remaining;
      return days != null
        ? `${days} day${days !== 1 ? "s" : ""} left in your free trial`
        : "Free trial";
    }
    if (state === "active") {
      return subscription?.cancel_at_period_end
        ? "Cancels at period end"
        : subscription?.plan_name || "Active";
    }
    if (state === "past_due") return "Payment failed — update your billing";
    if (state === "cancelled") return "Your plan was cancelled";
    return "Choose a plan to continue using features";
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Current Plan</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-3xl font-semibold">{heading}</p>
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
                chipCls,
              )}
            >
              {chipText}
            </span>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">{statusLabel()}</p>
          {subscription?.current_period_end &&
            subscription.state === "active" && (
              <p className="mt-0.5 text-xs text-muted-foreground/80">
                {subscription.cancel_at_period_end ? "Expires" : "Renews"}{" "}
                {new Date(subscription.current_period_end).toLocaleDateString()}
              </p>
            )}
        </div>
        <div className="flex flex-wrap gap-2">
          {hasActive && subscription?.has_billing_account && (
            <Button
              variant="outline"
              onClick={onPortal}
              disabled={portalPending}
            >
              {portalPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <SettingsIcon className="size-4" />
              )}
              Manage Subscription
            </Button>
          )}
          {!subscription?.platform_billed && (
            <Button onClick={onUpgrade}>
              <CreditCard className="size-4" />
              {hasActive ? "Change Plan" : "Upgrade Plan"}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function QuotaCard({ quota }: { quota: Record<string, QuotaItem> }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Resource Usage</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(quota).map(([key, item]) => {
            const pct =
              item.limit > 0 ? Math.min((item.used / item.limit) * 100, 100) : 0;
            const near = pct >= 80 && pct < 100;
            const at = pct >= 100;
            const barCls = at
              ? "bg-rose-500"
              : near
              ? "bg-amber-500"
              : "bg-indigo-500";
            const labelCls = at
              ? "text-rose-600 dark:text-rose-400"
              : near
              ? "text-amber-600 dark:text-amber-400"
              : "text-muted-foreground";
            return (
              <div key={key}>
                <div className="mb-1 flex items-center justify-between text-sm">
                  <span className="font-medium">
                    {QUOTA_LABELS[key] || key}
                  </span>
                  <span className={cn("tabular-nums", labelCls)}>
                    {item.used.toLocaleString()} /{" "}
                    {item.limit.toLocaleString()}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-muted">
                  <div
                    className={cn("h-full transition-all", barCls)}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function InvoicesCard({
  invoices,
}: {
  invoices: { id: string; status: string; amount_due_cents: number; period_start: string; pdf_url?: string | null; hosted_invoice_url?: string | null }[];
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Invoices</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {invoices.length === 0 ? (
          <p className="px-6 py-8 text-center text-sm text-muted-foreground">
            No invoices yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 font-medium">Invoice #</th>
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium">Amount</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {invoices.map((inv) => {
                  const url = inv.pdf_url || inv.hosted_invoice_url;
                  return (
                    <tr key={inv.id}>
                      <td className="px-4 py-2.5 font-mono text-xs">
                        {inv.id.slice(0, 8)}
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">
                        {new Date(inv.period_start).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums">
                        ${(inv.amount_due_cents / 100).toFixed(2)}
                      </td>
                      <td className="px-4 py-2.5">
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                            inv.status === "paid"
                              ? "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400"
                              : "text-muted-foreground bg-muted",
                          )}
                        >
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!url}
                          onClick={() =>
                            url &&
                            window.open(url, "_blank", "noopener,noreferrer")
                          }
                        >
                          <Download className="size-4" />
                          Download
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ChoosePlanDialog({
  open,
  onOpenChange,
  plans,
  subscription,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  plans: Plan[];
  subscription: Subscription | null;
}) {
  const [interval, setInterval] = useState<"monthly" | "annual">("monthly");

  const checkout = useMutation({
    mutationFn: (plan: Plan) =>
      billingApi.checkout({
        plan_id: plan.id,
        billing_interval: interval,
      }),
    onSuccess: (data) => {
      window.location.href = data.checkout_url;
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error || "Failed to start checkout.");
    },
  });

  function priceFor(plan: Plan) {
    const cents =
      interval === "monthly"
        ? plan.price_monthly_cents
        : plan.price_annual_cents;
    return (cents / 100).toFixed(0);
  }

  function isActive(plan: Plan) {
    return (
      subscription?.plan_slug === plan.slug &&
      subscription?.state === "active"
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Choose a Plan</DialogTitle>
          <DialogDescription>
            Switch between monthly and annual to compare pricing.
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-center">
          <div className="inline-flex rounded-md border bg-muted p-1 text-sm">
            <button
              type="button"
              onClick={() => setInterval("monthly")}
              className={cn(
                "rounded px-3 py-1 transition-colors",
                interval === "monthly"
                  ? "bg-background shadow"
                  : "text-muted-foreground",
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setInterval("annual")}
              className={cn(
                "rounded px-3 py-1 transition-colors",
                interval === "annual"
                  ? "bg-background shadow"
                  : "text-muted-foreground",
              )}
            >
              Annual
              <span className="ml-1.5 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-400">
                Save ~20%
              </span>
            </button>
          </div>
        </div>

        {plans.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No plans available yet. Contact support to upgrade.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {plans.map((p) => {
              const active = isActive(p);
              const pending =
                checkout.isPending && checkout.variables?.id === p.id;
              return (
                <Card
                  key={p.id}
                  className={cn(
                    "flex flex-col",
                    active && "border-primary ring-1 ring-primary/30",
                  )}
                >
                  <CardContent className="flex flex-1 flex-col gap-3 p-4">
                    <div className="flex items-center gap-2">
                      <p className="text-lg font-semibold">{p.name}</p>
                      {active && (
                        <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium uppercase text-primary">
                          Current
                        </span>
                      )}
                    </div>
                    <p className="text-2xl font-bold text-primary">
                      ${priceFor(p)}
                      <span className="ml-1 text-sm font-normal text-muted-foreground">
                        /{interval === "monthly" ? "mo" : "yr"}
                      </span>
                    </p>
                    {p.description && (
                      <p className="text-sm text-muted-foreground">
                        {p.description}
                      </p>
                    )}
                    {p.limits && Object.keys(p.limits).length > 0 && (
                      <ul className="space-y-1 text-sm">
                        {Object.entries(p.limits).map(([k, v]) => (
                          <li key={k} className="flex items-start gap-2">
                            <Check className="mt-0.5 size-4 shrink-0 text-emerald-500" />
                            <span>
                              {(v as number).toLocaleString()}{" "}
                              {LIMIT_LABELS[k] || k}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <Button
                      className="mt-auto"
                      variant={active ? "outline" : "default"}
                      disabled={active || pending}
                      onClick={() => checkout.mutate(p)}
                    >
                      {active
                        ? "Current Plan"
                        : pending
                        ? "Redirecting…"
                        : "Select"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Saved card + automatic recharge. A card on file is what makes one-click
 *  top-ups and Twilio-style auto-recharge possible — without one there is
 *  nothing to charge, so the auto-recharge controls stay disabled. */
function PaymentMethodCard({ packs }: { packs: CreditPack[] }) {
  const queryClient = useQueryClient();
  const cardQ = useQuery({
    queryKey: ["billing", "payment-method"],
    queryFn: billingApi.paymentMethod,
    staleTime: 30_000,
  });
  const autoQ = useQuery({
    queryKey: ["billing", "auto-topup"],
    queryFn: billingApi.autoTopUp,
    staleTime: 30_000,
  });

  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState("20");
  const [packId, setPackId] = useState<string>("");

  // Seed the form from the server once its values arrive.
  useEffect(() => {
    if (!autoQ.data) return;
    setEnabled(autoQ.data.enabled);
    if (autoQ.data.threshold_cents)
      setThreshold(String(autoQ.data.threshold_cents / 100));
    if (autoQ.data.pack_id) setPackId(autoQ.data.pack_id);
  }, [autoQ.data]);

  const saveCard = useMutation({
    mutationFn: () => billingApi.savePaymentMethod(),
    onSuccess: (d) => {
      window.location.href = d.checkout_url;
    },
    onError: () => toast.error("Could not start card setup."),
  });

  const removeCard = useMutation({
    mutationFn: () => billingApi.removePaymentMethod(),
    onSuccess: () => {
      toast.success("Card removed.");
      queryClient.invalidateQueries({ queryKey: ["billing"] });
    },
  });

  const saveAuto = useMutation({
    mutationFn: () =>
      billingApi.saveAutoTopUp({
        enabled,
        threshold_cents: Math.round(parseFloat(threshold || "0") * 100),
        pack_id: packId || null,
      }),
    onSuccess: () => {
      toast.success("Automatic top-up saved.");
      queryClient.invalidateQueries({ queryKey: ["billing", "auto-topup"] });
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: string } } };
      toast.error(e?.response?.data?.error || "Could not save.");
    },
  });

  const card = cardQ.data;
  const hasCard = !!card?.saved;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Payment Method</CardTitle>
        {/* No "add card" button: the card is kept from the credit purchase
            itself, so asking for it again is a step that buys nothing — and
            reads as if we were collecting card numbers ourselves. */}
        {hasCard && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => saveCard.mutate()}
              disabled={saveCard.isPending}
            >
              {saveCard.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <CreditCard className="size-4" />
              )}
              Replace
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => removeCard.mutate()}
              disabled={removeCard.isPending}
            >
              Remove
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-5">
        {hasCard ? (
          <p className="text-sm">
            <span className="font-medium capitalize">{card?.brand}</span> ••••{" "}
            {card?.last4}
            <span className="text-muted-foreground">
              {" "}
              · expires {String(card?.exp_month).padStart(2, "0")}/
              {card?.exp_year}
            </span>
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Buy credit once and your card is kept securely by Stripe — we only
            ever see the brand and last four digits. After that, top-ups are one
            click and automatic recharge can be switched on.
          </p>
        )}

        <div className="space-y-4 border-t pt-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label className="text-sm font-medium">Automatic top-up</Label>
              <p className="text-xs text-muted-foreground">
                Recharge automatically when the balance runs low, so calls never
                stop.
              </p>
            </div>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              disabled={!hasCard}
            />
          </div>

          {enabled && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="auto-threshold" className="text-xs">
                  When the balance falls below
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    $
                  </span>
                  <Input
                    id="auto-threshold"
                    className="pl-6"
                    inputMode="decimal"
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Recharge with</Label>
                <Select value={packId} onValueChange={setPackId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a credit pack" />
                  </SelectTrigger>
                  <SelectContent>
                    {packs.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} — ${(p.amount_cents / 100).toFixed(2)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <Button
            size="sm"
            onClick={() => saveAuto.mutate()}
            disabled={!hasCard || saveAuto.isPending}
          >
            {saveAuto.isPending && <Loader2 className="size-4 animate-spin" />}
            Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
